<script>
import {
  getInventoryTransfers,
  receiveInventoryTransfer,
} from '@/api/inventory.js'
import AppPagination from '@/components/common/AppPagination.vue'
import { getAdminInfo } from '@/utils/token.js'

const emptyReceiveForm = () => ({
  transferCode: '',
  productName: '',
  quantity: 0,
  memo: '',
})

export default {
  name: 'InventoryTransferPage',
  components: { AppPagination },
  data() {
    return {
      adminInfo: getAdminInfo(),
      search: {
        keyword: '',
        status: '',
      },
      statuses: [
        { title: '전체', value: '' },
        { title: '입고 대기', value: 'DISPATCHED' },
        { title: '입고 완료', value: 'RECEIVED' },
        { title: '취소', value: 'CANCELED' },
      ],
      items: [],
      total: 0,
      page: 1,
      size: 10,
      loading: false,
      saving: false,
      receiveDialog: false,
      receiveForm: emptyReceiveForm(),
      message: '',
      errorMessage: '',
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getInventoryTransfers({
          ...this.search,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', status: '' }
      this.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
    openReceive(item) {
      this.message = ''
      this.errorMessage = ''
      this.receiveForm = {
        transferCode: item.inventoryTransferRequestCode,
        productName: item.productName,
        quantity: Number(item.transferQty || 0),
        memo: '',
      }
      this.receiveDialog = true
    },
    async submitReceive() {
      if (!this.receiveForm.transferCode || Number(this.receiveForm.quantity) <= 0) {
        this.errorMessage = '입고 수량을 확인해주세요.'
        return
      }
      this.saving = true
      this.errorMessage = ''
      try {
        const res = await receiveInventoryTransfer(this.receiveForm.transferCode, {
          quantity: Number(this.receiveForm.quantity),
          memo: this.receiveForm.memo,
        })
        if (res.data?.code === 0) {
          this.receiveDialog = false
          this.message = '입고확정이 완료되었습니다. 해당 지점의 몰 판매 재고로 반영되었습니다.'
          await this.fetchList()
        } else {
          this.errorMessage = res.data?.message || '입고확정에 실패했습니다.'
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || '입고확정에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    canReceive(item) {
      if (item.transferStatus !== 'DISPATCHED') return false
      if (this.adminInfo.adminType === 'HQ') return true
      return this.adminInfo.branchCode && this.adminInfo.branchCode === item.toBranchCode
    },
    statusText(value) {
      const map = {
        DISPATCHED: '입고 대기',
        RECEIVED: '입고 완료',
        CANCELED: '취소',
      }
      return map[value] || value || '-'
    },
    statusColor(value) {
      const map = {
        DISPATCHED: 'warning',
        RECEIVED: 'success',
        CANCELED: 'grey',
      }
      return map[value] || 'grey'
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })
    },
    formatDateTime(value) {
      return value ? String(value).replace('T', ' ').substring(0, 16) : '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <div>
        <h1 class="page-title">지점 입고확인</h1>
        <p class="page-desc">본사에서 지점으로 불출된 상품을 확인하고 입고확정합니다. 입고확정된 수량만 몰 판매 가능 재고로 반영됩니다.</p>
      </div>
    </div>

    <v-alert v-if="message" type="success" variant="tonal" class="mb-3" closable @click:close="message = ''">
      {{ message }}
    </v-alert>
    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-3" closable @click:close="errorMessage = ''">
      {{ errorMessage }}
    </v-alert>

    <section class="search-panel">
      <div class="search-grid">
        <v-text-field
          v-model="search.keyword"
          placeholder="상품명, 지점명 검색"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          @keyup.enter="handleSearch"
        />
        <v-select v-model="search.status" :items="statuses" label="상태" variant="outlined" density="compact" hide-details />
        <div class="search-actions">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </div>
    </section>

    <section class="list-panel">
      <div class="list-title">
        <span>지점 불출/입고 목록</span>
        <v-chip size="small" color="primary" variant="tonal">{{ total }}건</v-chip>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table class="dark-table" density="comfortable">
        <thead>
          <tr>
            <th>상태</th>
            <th>상품</th>
            <th>불출 지점</th>
            <th>입고 지점</th>
            <th class="text-right">불출수량</th>
            <th class="text-right">입고수량</th>
            <th>불출일시</th>
            <th>입고일시</th>
            <th>메모</th>
            <th class="text-center">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.inventoryTransferRequestCode">
            <td>
              <v-chip size="small" :color="statusColor(item.transferStatus)" variant="tonal">
                {{ statusText(item.transferStatus) }}
              </v-chip>
            </td>
            <td><strong>{{ item.productName }}</strong></td>
            <td>{{ item.fromBranchName || '-' }}</td>
            <td>{{ item.toBranchName || '-' }}</td>
            <td class="text-right strong">{{ formatQty(item.transferQty) }}</td>
            <td class="text-right">{{ formatQty(item.receivedQty) }}</td>
            <td>{{ formatDateTime(item.dispatchedDt) }}</td>
            <td>{{ formatDateTime(item.receivedDt) }}</td>
            <td class="memo-cell">
              <div v-if="item.dispatchMemo"><strong>불출</strong> {{ item.dispatchMemo }}</div>
              <div v-if="item.receiveMemo"><strong>입고</strong> {{ item.receiveMemo }}</div>
              <span v-if="!item.dispatchMemo && !item.receiveMemo" class="muted">-</span>
            </td>
            <td class="text-center">
              <v-btn v-if="canReceive(item)" size="small" color="primary" variant="tonal" @click="openReceive(item)">
                입고확정
              </v-btn>
              <span v-else class="muted">-</span>
            </td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="10" class="empty-cell">지점 불출/입고 내역이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
    </section>

    <v-dialog v-model="receiveDialog" max-width="560">
      <v-card class="dialog-card">
        <v-card-title>지점 입고확정</v-card-title>
        <v-card-text>
          <div class="readonly-box mb-4">
            <span>상품</span>
            <strong>{{ receiveForm.productName }}</strong>
          </div>
          <v-alert color="info" variant="tonal" class="mb-4">
            입고확정 수량은 해당 지점의 몰 판매 가능 재고로 반영됩니다.
          </v-alert>
          <v-row>
            <v-col cols="12">
              <v-text-field v-model.number="receiveForm.quantity" type="number" label="입고 수량 *" variant="outlined" min="0.001" step="0.001" />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="receiveForm.memo" label="입고 메모" variant="outlined" rows="4" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="receiveDialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitReceive">입고확정</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.page-title { font-size: 24px; font-weight: 800; margin: 0; color: #f8fafc; }
.page-desc { margin: 6px 0 0; color: #a9b6c9; font-size: 13px; }
.search-panel, .list-panel { border: 1px solid #334155; background: #1c222e; border-radius: 8px; padding: 16px; }
.search-panel { margin-bottom: 16px; }
.search-grid { display: grid; grid-template-columns: minmax(260px, 1fr) 180px auto; gap: 10px; align-items: center; }
.search-actions { display: flex; gap: 8px; justify-content: flex-end; }
.list-title { display: flex; align-items: center; gap: 8px; font-size: 17px; font-weight: 800; color: #f8fafc; margin-bottom: 12px; }
.dark-table { border: 1px solid #334155; border-radius: 8px; overflow: hidden; background: #111827; }
.dark-table :deep(th) { background: #252d3b !important; color: #dbeafe !important; font-weight: 800 !important; white-space: nowrap; }
.dark-table :deep(td) { color: #e5e7eb !important; border-bottom: 1px solid #243041 !important; vertical-align: middle; }
.strong { font-weight: 800; }
.memo-cell { max-width: 240px; white-space: normal; color: #cbd5e1 !important; }
.memo-cell strong { color: #fbbf24; margin-right: 4px; }
.muted { color: #94a3b8; font-size: 12px; }
.empty-cell { text-align: center; color: #94a3b8 !important; padding: 36px !important; }
.dialog-card { background: #1c222e !important; color: #e5e7eb !important; border: 1px solid #334155; }
.readonly-box { border: 1px solid #334155; background: #111827; border-radius: 8px; padding: 12px 14px; display: grid; gap: 4px; }
.readonly-box span { color: #94a3b8; font-size: 12px; }
.readonly-box strong { color: #f8fafc; }
@media (max-width: 900px) {
  .search-grid { grid-template-columns: 1fr; }
  .search-actions { justify-content: flex-start; }
}
</style>
